// The company sign-in (04-sign-in.md): the password checked by the mail server, the chat sign-in
// through the company's account service, whose legacy single sign-on desyr runs itself — in the
// background, or through the account service's own page when the background can't be used.
import type { CompanyEntry, Config } from '../core/config';
import { connectMail, keepPending, passwordCheck } from '../core/mail';
import { SignInError } from './server';

/** The account service's request to the mail server (the query of its authorisation address). */
export type AccountRequest = Record<string, string>;

export type CompanyResult =
  | { kind: 'token'; token: string }
  | { kind: 'code' }
  | { kind: 'refused' }
  /** Gone to the account service's page; it comes back with the token. */
  | { kind: 'away' };

const REQUEST = 'desyr_account_request';
const STARTED = 'desyr_account_started';
const PAGE = 'desyr_account_page';
/** The page way's note that a password was checked in this tab (04, step 3): used once, 15 minutes. */
const CHECKED = 'desyr_password_checked';
/** When this tab last ended a token nobody typed a password for (04, step 4). */
const ENDED = 'desyr_token_ended';
const memory: Record<string, string | undefined> = {};

function tab(key: string, value?: string | null): string | undefined {
  if (value !== undefined) {
    memory[key] = value ?? undefined;
    try {
      if (value === null) sessionStorage.removeItem(key);
      else sessionStorage.setItem(key, value);
    } catch {
      // memory only
    }
    return undefined;
  }
  try {
    return sessionStorage.getItem(key) ?? memory[key];
  } catch {
    return memory[key];
  }
}

const masOrigin = (entry: CompanyEntry) => new URL(entry.mas!).origin;
const done = (entry: CompanyEntry) => `${masOrigin(entry)}/_desyr/login/done`;

/** The account service's own page is used for another site, when the entry says so, or after a failure. */
export function usesPage(entry: CompanyEntry): boolean {
  if (tab(PAGE) === masOrigin(entry)) return true;
  if (entry.masPage !== undefined) return entry.masPage;
  return new URL(entry.mas!).hostname !== location.hostname;
}

class Unreadable extends Error {}

/** A fetch whose answer can't be read (no connection, the router's CORS missing). */
export async function read(url: string, init: RequestInit = {}): Promise<Response> {
  try {
    return await fetch(url, { credentials: 'include', cache: 'no-store', ...init });
  } catch {
    throw new Unreadable();
  }
}

interface PageForm {
  url: string;
  fields: URLSearchParams;
  /** Only hidden fields: a confirmation ("Continue"), not a question for the person. */
  confirmOnly: boolean;
}

/** The form on one of the account service's pages: its address and its fields (form token `csrf`). */
export async function formOf(answer: Response): Promise<PageForm | undefined> {
  const html = await answer.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const form = doc.querySelector('form');
  const csrf = form?.querySelector<HTMLInputElement>('input[name=csrf]')?.value;
  if (!form || !csrf) return undefined;
  const inputs = [...form.querySelectorAll<HTMLInputElement>('input[name]')];
  const fields = new URLSearchParams();
  inputs.forEach((i) => fields.append(i.name, i.value));
  return {
    url: new URL(form.getAttribute('action') || answer.url, answer.url).href,
    fields,
    confirmOnly: inputs.every((i) => i.type === 'hidden'),
  };
}

/**
 * A leftover "Continue" or "Session terminated" page (04, step 3): its form token (the one form's
 * hidden `csrf`) posted to the account service's own sign-out address, with its cookies.
 */
async function signOutAt(entry: CompanyEntry, answer: Response): Promise<boolean> {
  const csrf = (await formOf(answer))?.fields.get('csrf');
  if (!csrf) return false;
  await read(`${entry.mas!.replace(/\/+$/, '')}/logout`, { method: 'POST', body: new URLSearchParams({ csrf }) });
  return true;
}

const isRequest = (url: URL) => url.searchParams.has('code_challenge') && url.searchParams.has('redirect_uri');

/** Step 2–3: the single sign-on start, clearing a leftover or finished session once. */
async function startBackground(entry: CompanyEntry): Promise<AccountRequest> {
  for (let attempt = 0; attempt < 2; attempt++) {
    const answer = await read(`${entry.mas!.replace(/\/+$/, '')}/_matrix/client/v3/login/sso/redirect?redirectUrl=${encodeURIComponent(done(entry))}`);
    const end = new URL(answer.url);
    if (answer.status === 204 && isRequest(end)) return Object.fromEntries(end.searchParams);
    // "Continue" from before, or "Session terminated": post its own sign-out form and start fresh.
    // Never confirmed here: only step 5's "Continue", after the typed address's password, is (04).
    if (!(answer.ok && (await signOutAt(entry, answer)))) break;
  }
  // A leftover session that couldn't be cleared: the error line on the form, nothing navigates.
  throw new SignInError('leftover');
}

/** Step 5: back to the account service with the code; its "Continue" confirmed; the token. */
async function finishBackground(entry: CompanyEntry, request: AccountRequest, code: string, iss?: string): Promise<CompanyResult> {
  const callback = new URL(new URL(request.redirect_uri).pathname, masOrigin(entry));
  callback.search = new URLSearchParams({ code, state: request.state, ...(iss ? { iss } : {}) }).toString();
  let answer = await read(callback.href);
  // "Continue" is a page on the account service's own origin with a form token and only hidden fields.
  const form = answer.ok && new URL(answer.url).origin === masOrigin(entry) ? await formOf(answer.clone()) : undefined;
  // A page that asks the person something: the error step with a reference, never that page
  // (the owner's interim reading of step 6, item 58).
  if (form && !form.confirmOnly) throw new SignInError('company-question');
  if (form) answer = await read(form.url, { method: 'POST', body: form.fields });
  const token = new URL(answer.url).searchParams.get('loginToken');
  if (answer.status === 204 && token) return { kind: 'token', token };
  throw new SignInError('company'); // read, but not what was expected: the error step, no navigation
}

/** The tab's password note, taken (used once): a password was checked here in the last 15 minutes. */
export function takePasswordNote(): boolean {
  const at = Number(tab(CHECKED) ?? 0);
  tab(CHECKED, null);
  return at > 0 && Date.now() - at < 15 * 60_000;
}

/**
 * A token nobody typed a password for (04, the page way's step 4): signed in with only to end it,
 * at the company's sign-in service and then the chat session itself. False when such a token came
 * back again straight away (within 30 s): the error step instead of another round.
 */
export async function endUntypedToken(base: string, token: string, end: (s: { user_id: string; access_token: string }) => Promise<void>): Promise<boolean> {
  const again = Date.now() - Number(tab(ENDED) ?? 0) < 30_000;
  tab(ENDED, String(Date.now()));
  const answer = await fetch(`${base}/_matrix/client/v3/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'm.login.token', token }) }).catch(() => undefined);
  const session = answer?.ok ? await answer.json().catch(() => undefined) : undefined;
  if (session?.access_token) {
    await end(session);
    await fetch(`${base}/_matrix/client/v3/logout`, { method: 'POST', headers: { Authorization: `Bearer ${session.access_token}` } }).catch(() => {});
  }
  return !again;
}

/** After such a token: the account service's "Session terminated" page cleared through its sign-out address, where readable. */
export async function clearEnded(entry: CompanyEntry): Promise<void> {
  try {
    const answer = await read(`${entry.mas!.replace(/\/+$/, '')}/_matrix/client/v3/login/sso/redirect?redirectUrl=${encodeURIComponent(done(entry))}`);
    if (answer.ok) await signOutAt(entry, answer);
  } catch {
    // unreadable: the page way shows the account service's own page then
  }
}

/** Begins the account service's page way for this company (04, "… through the account service's page"). */
export function startPage(entry: CompanyEntry): void {
  const last = Number(tab(STARTED) ?? 0);
  if (Date.now() - last < 30_000) throw new SignInError('loop');
  tab(STARTED, String(Date.now()));
  location.replace(`${entry.mas!.replace(/\/+$/, '')}/_matrix/client/v3/login/sso/redirect?redirectUrl=${encodeURIComponent(`${location.origin}/login`)}`);
}

/** "Try again" after the loop guard stopped: the next start goes ahead anyway. */
export const resetPageGuard = () => tab(STARTED, null);
/** 04's own sign-in through a server's sign-in page, started on this device and still waiting for its token. */
export const pageWaiting = () => Date.now() - Number(tab(STARTED) ?? 0) < 15 * 60_000;

/** A fresh request (under 15 minutes old) the account service's page sent for this server. */
export function pageRequest(server: string): AccountRequest | undefined {
  try {
    const kept = JSON.parse(tab(REQUEST) ?? 'null') as { server: string; at: number; request: AccountRequest } | null;
    return kept && kept.server === server && Date.now() - kept.at < 15 * 60_000 ? kept.request : undefined;
  } catch {
    return undefined;
  }
}

/**
 * At start, before the routes: the account service's request arriving at desyr's authorisation
 * address is taken only with a code challenge and a return address under `/upstream/callback/` on
 * the origin of a configured account service that uses the page way. Returns its server.
 */
export function takeAccountRequest(config: Config, found: Record<string, CompanyEntry>): string | undefined {
  if (!/^\/_desyr\/login\/[^/]+\/authorize\/?$/.test(location.pathname)) return undefined;
  const url = new URL(location.href);
  history.replaceState(null, '', '/login');
  if (!isRequest(url)) return undefined;
  const back = new URL(url.searchParams.get('redirect_uri')!, location.origin);
  for (const [server, entry] of Object.entries({ ...found, ...config.desyrSignIn })) {
    if (!entry.mas || !usesPage(entry)) continue;
    if (back.origin !== masOrigin(entry) || !back.pathname.startsWith('/upstream/callback/')) continue;
    tab(REQUEST, JSON.stringify({ server, at: Date.now(), request: Object.fromEntries(url.searchParams) }));
    tab(STARTED, null);
    return server;
  }
  return undefined;
}

export interface CompanyAttempt {
  server: string;
  entry: CompanyEntry;
  email: string;
  password: string;
  /** The two-step code, on the code step. */
  code?: string;
}

// The account service's request of a background start, kept for the code step.
let pending: { server: string; request: AccountRequest } | undefined;

/**
 * The company form's "Sign in" (and the code step's "Confirm"). Unreadable answers switch this tab to
 * the account service's page way and start it at once (step 7).
 */
export async function companySignIn(a: CompanyAttempt): Promise<CompanyResult> {
  const page = usesPage(a.entry);
  let request = page ? pageRequest(a.server) : a.code && pending?.server === a.server ? pending.request : undefined;
  try {
    if (!request) {
      if (page) {
        startPage(a.entry);
        return { kind: 'away' };
      }
      request = await startBackground(a.entry);
    }
    const answer = await passwordCheck(a.entry.password!, {
      accountName: a.email,
      accountSecret: a.password,
      clientId: request.client_id,
      redirectUri: request.redirect_uri,
      scope: request.scope,
      state: request.state,
      nonce: request.nonce,
      codeChallenge: request.code_challenge,
      codeChallengeMethod: request.code_challenge_method,
      mfaToken: a.code,
    });
    if (answer.type === 'mfaRequired') {
      pending = { server: a.server, request };
      return { kind: 'code' };
    }
    if (answer.type === 'failure') return { kind: 'refused' };
    pending = undefined;
    // The same password also gets the mail access (23); chat signs in even when that fails.
    if (a.entry.mail)
      await connectMail(a.entry.password!, a.entry.mail, a.email, a.password, a.code).then((m) => typeof m === 'object' && keepPending(m), () => {});
    if (page) {
      tab(REQUEST, null);
      tab(CHECKED, String(Date.now()));
      const callback = new URL(new URL(request.redirect_uri).pathname, masOrigin(a.entry));
      callback.search = new URLSearchParams({ code: answer.code, state: request.state, ...(answer.iss ? { iss: answer.iss } : {}) }).toString();
      location.replace(callback.href);
      return { kind: 'away' };
    }
    return await finishBackground(a.entry, request, answer.code, answer.iss);
  } catch (e) {
    if (e instanceof Unreadable && !page) {
      tab(PAGE, masOrigin(a.entry));
      startPage(a.entry);
      return { kind: 'away' };
    }
    throw e instanceof SignInError ? e : new SignInError('company');
  }
}
